import Combobox from "@/components/Combobox"
import FormHeader from "@/components/FormHeader"
import InputError from "@/components/InputError"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listBentukPendidikan } from "@/constants"
import api from "@/lib/axios"
import { getListWilayah } from "@/services/getData"
import { Option, Sekolah, Wilayah } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: Sekolah) => {
  const response = await api.post("/master/sekolah/store", newData)
  return response.data
}

const patchData = async (newData: Sekolah) => {
  const response = await api.patch("/master/sekolah/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_sekolah: number }) => {
  const response = await api({
    url: "/master/sekolah/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Sekolah | null
  closeModal: () => void
}) {
  const [openProvinsi, setOpenProvinsi] = useState(false)
  const [openKabupaten, setOpenKabupaten] = useState(false)
  const [openKecamatan, setOpenKecamatan] = useState(false)
  const [idFeederProvinsi, setIdFeederProvinsi] = useState("")
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("")
  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("")
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("")
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("")
  const [form, setForm] = useState<
    Sekolah & {
      provinsi: string
      kabupaten: string
      kecamatan: string
    }
  >({
    id_ref_sekolah: selectedRow?.id_ref_sekolah || 0,
    id_ref_wilayah: selectedRow?.id_ref_wilayah || 0,
    npsn_sekolah: selectedRow?.npsn_sekolah || "",
    nama_sekolah: selectedRow?.nama_sekolah || "",
    bentuk_pendidikan: selectedRow?.bentuk_pendidikan || "",
    provinsi: "",
    kabupaten: "",
    kecamatan: "",
  })

  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  })

  const {
    data: dataKabupaten,
    // refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  })

  const {
    data: dataKecamatan,
    // refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSekolah"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate({ ...form, id_ref_wilayah: Number(form.kecamatan) })
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Sekolah"
          description={`Apakah anda yakin akan menghapus data Sekolah "${selectedRow?.nama_sekolah}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Sekolah" : "Update Data Sekolah"}
      />
      <div>
        <Label>Provinsi</Label>
        <Combobox
          serverSide
          open={openProvinsi}
          setOpen={setOpenProvinsi}
          value={form.provinsi || ""}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              provinsi: newValue,
              kabupaten: "",
              kecamatan: "",
            }))
            const selectedProvinsi = dataProvinsi?.find(
              (provinsi: Option) => provinsi?.value == newValue
            )
            setIdFeederProvinsi(selectedProvinsi?.id_feeder)
          }}
          options={dataProvinsi}
          placeholder="Pilih provinsi"
          onChange={(event) => setSearchQueryProvinsi(event.target.value)}
          searchQueryValue={searchQueryProvinsi || ""}
          isPending={isPendingProvinsi}
        />
        <InputError text={error?.response?.data?.errors?.provinsi?.join(",")} />
      </div>
      <div>
        <Label>Kabupaten</Label>
        <Combobox
          serverSide
          open={openKabupaten}
          setOpen={setOpenKabupaten}
          value={form.kabupaten || ""}
          setValue={(newValue) => {
            setForm((prev) => ({
              ...prev,
              kabupaten: newValue,
              kecamatan: "",
            }))
            const selectedKabupaten = dataKabupaten?.find(
              (kabupaten: Option) => kabupaten?.value == newValue
            )
            setIdFeederKabupaten(selectedKabupaten?.id_feeder)
          }}
          options={dataKabupaten}
          placeholder="Pilih kabupaten"
          onChange={(event) => setSearchQueryKabupaten(event.target.value)}
          searchQueryValue={searchQueryKabupaten || ""}
          isPending={isPendingKabupaten}
        />
        <InputError
          text={error?.response?.data?.errors?.kabupaten?.join(",")}
        />
      </div>

      <div>
        <Label>Kecamatan</Label>
        <Combobox
          serverSide
          open={openKecamatan}
          setOpen={setOpenKecamatan}
          value={form.kecamatan || ""}
          setValue={(newValue) =>
            setForm((prev) => ({
              ...prev,
              kecamatan: newValue,
            }))
          }
          options={dataKecamatan}
          placeholder="Pilih kecamatan"
          onChange={(event) => setSearchQueryKecamatan(event.target.value)}
          searchQueryValue={searchQueryKecamatan || ""}
          isPending={isPendingKecamatan}
        />
        <InputError
          text={error?.response?.data?.errors?.kecamatan?.join(",")}
        />
      </div>
      <div>
        <Label>NPSN Sekolah</Label>
        <Input
          placeholder="Ketik npsn sekolah..."
          value={form.npsn_sekolah}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, npsn_sekolah: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Nama Sekolah</Label>
        <Input
          placeholder="Ketik nama sekolah..."
          value={form.nama_sekolah}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nama_sekolah: event.target.value }))
          }
        />
      </div>

      <div>
        <Label>Bentuk Pendidikan</Label>
        <Select
          value={form.bentuk_pendidikan}
          onValueChange={(value) => {
            setForm((prev) => ({
              ...prev,
              bentuk_pendidikan: value,
            }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih bentuk pendidikan"} />
          </SelectTrigger>
          <SelectContent>
            {listBentukPendidikan?.map((value, index) => (
              <SelectItem key={index} value={`${value}`}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
